import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Globe, Calendar, User, Shield, Loader2, Copy } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface WhoisInfo {
  domain: string;
  registrar?: string;
  registrant?: {
    name?: string;
    organization?: string;
    country?: string;
    email?: string;
  };
  dates?: {
    created?: string;
    updated?: string;
    expires?: string;
  };
  nameservers?: string[];
  status?: string[];
  dnssec?: boolean;
  rawData?: string;
}

const WhoisLookupTool: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [whoisInfo, setWhoisInfo] = useState<WhoisInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useSEO({
    title: 'WHOIS Lookup Tool - Domain Registration & Owner Information',
    description: 'Look up domain registration details, owner information, expiration dates, and nameservers. Get comprehensive WHOIS data for any domain.',
    keywords: 'whois lookup, domain lookup, domain registration, domain owner, domain expiration, nameservers, registrar information',
    canonical: 'https://snap-tools.online/security/whois-lookup-tool'
  });

  // Validate domain format
  const isValidDomain = (domainName: string): boolean => {
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(domainName);
  };

  // Clean domain input
  const cleanDomain = (input: string): string => {
    let cleaned = input.trim().toLowerCase();
    // Remove protocol if present
    cleaned = cleaned.replace(/^https?:\/\//, '');
    // Remove www if present
    cleaned = cleaned.replace(/^www\./, '');
    // Remove trailing slash
    cleaned = cleaned.replace(/\/$/, '');
    // Extract domain part if URL path is present
    cleaned = cleaned.split('/')[0];
    return cleaned;
  };

  // Mock WHOIS lookup (in real implementation, you'd use a WHOIS API)
  const lookupWhois = async () => {
    const cleanedDomain = cleanDomain(domain);
    
    if (!cleanedDomain) {
      toast.error('Please enter a domain name');
      return;
    }

    if (!isValidDomain(cleanedDomain)) {
      toast.error('Please enter a valid domain name');
      return;
    }

    setIsLoading(true);
    setWhoisInfo(null);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock data for demonstration (in real implementation, use WHOIS API)
      const mockWhoisInfo: WhoisInfo = {
        domain: cleanedDomain,
        registrar: 'Example Registrar LLC',
        registrant: {
          name: 'Privacy Protected',
          organization: 'Privacy Service',
          country: 'United States',
          email: 'privacy@example.com'
        },
        dates: {
          created: '2020-01-15T00:00:00Z',
          updated: '2023-06-20T12:30:00Z',
          expires: '2025-01-15T23:59:59Z'
        },
        nameservers: [
          'ns1.example.com',
          'ns2.example.com',
          'ns3.example.com'
        ],
        status: [
          'clientTransferProhibited',
          'clientDeleteProhibited',
          'clientUpdateProhibited'
        ],
        dnssec: false,
        rawData: `Domain Name: ${cleanedDomain.toUpperCase()}\nRegistry Domain ID: 123456789_DOMAIN_COM-VRSN\nRegistrar WHOIS Server: whois.example.com\nRegistrar URL: http://www.example.com\nUpdated Date: 2023-06-20T12:30:00Z\nCreation Date: 2020-01-15T00:00:00Z\nRegistry Expiry Date: 2025-01-15T23:59:59Z\nRegistrar: Example Registrar LLC\nRegistrar IANA ID: 12345\nRegistrar Abuse Contact Email: abuse@example.com\nRegistrar Abuse Contact Phone: +1.5551234567\nDomain Status: clientTransferProhibited\nDomain Status: clientDeleteProhibited\nDomain Status: clientUpdateProhibited\nName Server: NS1.EXAMPLE.COM\nName Server: NS2.EXAMPLE.COM\nName Server: NS3.EXAMPLE.COM\nDNSSEC: unsigned`
      };

      setWhoisInfo(mockWhoisInfo);
      toast.success('WHOIS information retrieved successfully');
    } catch (error) {
      console.error('Error looking up WHOIS:', error);
      toast.error('Failed to retrieve WHOIS information. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Calculate days until expiration
  const getDaysUntilExpiration = (expirationDate: string): number => {
    try {
      const expiry = new Date(expirationDate);
      const today = new Date();
      const diffTime = expiry.getTime() - today.getTime();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch {
      return 0;
    }
  };

  // Copy information to clipboard
  const copyInfo = async (info: string, label: string) => {
    try {
      await navigator.clipboard.writeText(info);
      toast.success(`${label} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  // Example domains for testing
  const exampleDomains = [
    'google.com',
    'github.com',
    'stackoverflow.com',
    'mozilla.org',
    'wikipedia.org'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Search className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">WHOIS Lookup Tool</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Get detailed domain registration information including owner details, registration dates, nameservers, and more. Essential for domain research and due diligence.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Domain Input Section */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domain Name:
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="Enter domain name (e.g., example.com)"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && lookupWhois()}
                  />
                  <button
                    onClick={lookupWhois}
                    disabled={isLoading || !domain.trim()}
                    className="flex items-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                    {isLoading ? 'Looking up...' : 'Lookup'}
                  </button>
                </div>
              </div>

              {/* Example Domains */}
              <div>
                <p className="text-sm text-gray-600 mb-2">Try these examples:</p>
                <div className="flex flex-wrap gap-2">
                  {exampleDomains.map((example) => (
                    <button
                      key={example}
                      onClick={() => setDomain(example)}
                      className="text-sm px-3 py-1 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* WHOIS Information Results */}
          {whoisInfo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6 mb-6"
            >
              {/* Domain Overview */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="w-5 h-5 text-gray-700" />
                  <h3 className="text-lg font-semibold text-gray-900">Domain Overview</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Domain:</span>
                      <span className="font-medium">{whoisInfo.domain}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Registrar:</span>
                      <span className="font-medium">{whoisInfo.registrar || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">DNSSEC:</span>
                      <span className={`font-medium ${whoisInfo.dnssec ? 'text-green-600' : 'text-red-600'}`}>
                        {whoisInfo.dnssec ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                  </div>
                  
                  {whoisInfo.dates?.expires && (
                    <div className="bg-white rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Expiration Status</h4>
                      <div className="text-sm">
                        <div className="text-gray-600">Expires: {formatDate(whoisInfo.dates.expires)}</div>
                        <div className={`font-medium ${
                          getDaysUntilExpiration(whoisInfo.dates.expires) < 30 
                            ? 'text-red-600' 
                            : getDaysUntilExpiration(whoisInfo.dates.expires) < 90 
                              ? 'text-orange-600' 
                              : 'text-green-600'
                        }`}>
                          {getDaysUntilExpiration(whoisInfo.dates.expires)} days remaining
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Registration Dates */}
              {whoisInfo.dates && (
                <div className="bg-white rounded-lg p-6 border">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Registration Dates</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    {whoisInfo.dates.created && (
                      <div className="bg-green-50 p-3 rounded">
                        <div className="font-medium text-green-900">Created</div>
                        <div className="text-green-700">{formatDate(whoisInfo.dates.created)}</div>
                      </div>
                    )}
                    {whoisInfo.dates.updated && (
                      <div className="bg-blue-50 p-3 rounded">
                        <div className="font-medium text-blue-900">Updated</div>
                        <div className="text-blue-700">{formatDate(whoisInfo.dates.updated)}</div>
                      </div>
                    )}
                    {whoisInfo.dates.expires && (
                      <div className={`p-3 rounded ${
                        getDaysUntilExpiration(whoisInfo.dates.expires) < 30 
                          ? 'bg-red-50' 
                          : getDaysUntilExpiration(whoisInfo.dates.expires) < 90 
                            ? 'bg-orange-50' 
                            : 'bg-yellow-50'
                      }`}>
                        <div className={`font-medium ${
                          getDaysUntilExpiration(whoisInfo.dates.expires) < 30 
                            ? 'text-red-900' 
                            : getDaysUntilExpiration(whoisInfo.dates.expires) < 90 
                              ? 'text-orange-900' 
                              : 'text-yellow-900'
                        }`}>Expires</div>
                        <div className={`${
                          getDaysUntilExpiration(whoisInfo.dates.expires) < 30 
                            ? 'text-red-700' 
                            : getDaysUntilExpiration(whoisInfo.dates.expires) < 90 
                              ? 'text-orange-700' 
                              : 'text-yellow-700'
                        }`}>{formatDate(whoisInfo.dates.expires)}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Registrant Information */}
              {whoisInfo.registrant && (
                <div className="bg-white rounded-lg p-6 border">
                  <div className="flex items-center gap-2 mb-4">
                    <User className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Registrant Information</h3>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-3">
                      {whoisInfo.registrant.name && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium">{whoisInfo.registrant.name}</span>
                        </div>
                      )}
                      {whoisInfo.registrant.organization && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Organization:</span>
                          <span className="font-medium">{whoisInfo.registrant.organization}</span>
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {whoisInfo.registrant.country && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Country:</span>
                          <span className="font-medium">{whoisInfo.registrant.country}</span>
                        </div>
                      )}
                      {whoisInfo.registrant.email && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium">{whoisInfo.registrant.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Nameservers */}
              {whoisInfo.nameservers && whoisInfo.nameservers.length > 0 && (
                <div className="bg-white rounded-lg p-6 border">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-gray-700" />
                      <h3 className="text-lg font-semibold text-gray-900">Nameservers</h3>
                    </div>
                    <button
                      onClick={() => copyInfo(whoisInfo.nameservers!.join('\n'), 'Nameservers')}
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-2">
                    {whoisInfo.nameservers.map((ns, index) => (
                      <div key={index} className="font-mono text-sm bg-gray-50 p-3 rounded">
                        {ns}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Domain Status */}
              {whoisInfo.status && whoisInfo.status.length > 0 && (
                <div className="bg-white rounded-lg p-6 border">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Domain Status</h3>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {whoisInfo.status.map((status, index) => (
                      <span
                        key={index}
                        className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {status}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw WHOIS Data */}
              {whoisInfo.rawData && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Raw WHOIS Data</h3>
                    <button
                      onClick={() => copyInfo(whoisInfo.rawData!, 'Raw WHOIS Data')}
                      className="flex items-center gap-1 text-purple-600 hover:text-purple-700 text-sm"
                    >
                      <Copy className="w-4 h-4" />
                      Copy
                    </button>
                  </div>
                  
                  <div className="bg-white rounded border p-4 max-h-64 overflow-y-auto">
                    <pre className="font-mono text-xs text-gray-700 whitespace-pre-wrap">
                      {whoisInfo.rawData}
                    </pre>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">What is WHOIS?</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• <strong>Domain Database:</strong> Public registry of domain ownership</li>
                <li>• <strong>Contact Information:</strong> Details about domain registrant</li>
                <li>• <strong>Registration Dates:</strong> When domain was created and expires</li>
                <li>• <strong>Technical Details:</strong> Nameservers and DNS configuration</li>
                <li>• <strong>Legal Tool:</strong> Used for trademark and legal disputes</li>
                <li>• <strong>Security Research:</strong> Investigate suspicious domains</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Common Use Cases</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• <strong>Domain Research:</strong> Check availability and ownership history</li>
                <li>• <strong>Due Diligence:</strong> Verify business domain ownership</li>
                <li>• <strong>Expired Domains:</strong> Find domains available for registration</li>
                <li>• <strong>Security Analysis:</strong> Investigate suspicious or malicious domains</li>
                <li>• <strong>Competitive Research:</strong> Learn about competitor domains</li>
                <li>• <strong>Legal Matters:</strong> Support trademark and copyright claims</li>
              </ul>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="font-semibold text-yellow-900 mb-2">Privacy & Limitations</h4>
            <div className="text-sm text-yellow-800 space-y-1">
              <p>• Many domains now use privacy protection services to hide personal information</p>
              <p>• GDPR and other privacy laws have limited the amount of public information available</p>
              <p>• Some TLDs may have restricted WHOIS access or different data formats</p>
              <p>• Information may be cached and not reflect the most recent changes</p>
              <p>• This tool provides example data for demonstration purposes</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhoisLookupTool;
